import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import { toast } from "sonner";

const ResetPassword = () => {
  const { t, setLangAuto } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // MF-3 i18n (May 2026) — apply the user's preferred language for the
  // reset-password landing. The backend exposes a minimal meta endpoint
  // that returns ONLY `{language}` and never consumes the token. If the
  // token is missing/expired the response is the safe default ("en"),
  // so we never leak token validity through the language field.
  // setLangAuto respects an existing manual choice from the same browser.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.get(`/auth/reset-token-meta?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        const userLang = res?.data?.language;
        if (userLang) setLangAuto(userLang);
      })
      .catch(() => { /* fail-open: keep the current language */ });
    return () => { cancelled = true; };
  }, [token, setLangAuto]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t("phase2.missingResetToken"));
      return;
    }
    if (pwd.length < 8) {
      toast.error(t("phase2.passwordTooShort"));
      return;
    }
    if (pwd !== confirm) {
      toast.error(t("phase2.passwordsMismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success(t("phase2.passwordUpdatedTitle"));
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="tsw-section py-16 max-w-md">
        <div className="mb-8">
          <div className="tsw-overline mb-2">{t("common.login")}</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900" data-testid="reset-heading">
            {t("phase2.resetHeading")}
          </h1>
          <p className="text-slate-600 mt-2">{t("phase2.resetSub")}</p>
        </div>

        {done ? (
          <div className="tsw-card p-6 sm:p-8 space-y-4" data-testid="reset-success">
            <h2 className="font-display font-semibold text-xl text-slate-900">{t("phase2.passwordUpdatedTitle")}</h2>
            <p className="text-slate-700">{t("phase2.passwordUpdatedBody")}</p>
            <Link to="/login" className="tsw-btn-primary" data-testid="reset-to-login">
              {t("phase2.signIn")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="tsw-card p-6 sm:p-8 space-y-5" data-testid="reset-form">
            {!token && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" data-testid="reset-no-token">
                {t("phase2.missingResetToken")}
              </div>
            )}
            <div>
              <label className="tsw-label">{t("phase2.newPasswordLabel")}</label>
              <input
                type="password"
                required
                minLength={8}
                className="tsw-input"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                data-testid="reset-password"
              />
            </div>
            <div>
              <label className="tsw-label">{t("phase2.confirmPasswordLabel")}</label>
              <input
                type="password"
                required
                minLength={8}
                className="tsw-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
              />
              {confirm && pwd !== confirm && (
                <p className="mt-1 text-sm text-red-600" data-testid="reset-mismatch">
                  {t("phase2.passwordsMismatch")}
                </p>
              )}
            </div>
            <button type="submit" className="tsw-btn-primary w-full" disabled={loading || !token} data-testid="reset-submit">
              {loading ? t("common.loading") : t("phase2.saveNewPassword")}
            </button>
            <div className="text-center text-sm text-slate-500">
              <Link to="/login" className="text-emerald-700 font-medium" data-testid="reset-to-login">
                {t("phase2.backToLogin")}
              </Link>
            </div>
          </form>
        )}
      </div>
    </PublicLayout>
  );
};

export default ResetPassword;
